import React, { useEffect, useState, ChangeEvent } from 'react';
import { FieldError, Input } from '@ktag/styleguide/dist/react';

import { useTranslation } from 'react-i18next';
import { Point } from 'geojson';
import useAppState from '../../../AppStore';
import DrawControlsLabel from './DrawControlsLabel';
import { useTracking } from '../../../utils/tracking/TrackingContext';

const round = (radius: number) => {
  return Math.round(radius * 100) / 100;
};

const CoordinatesSetting = () => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();
  const selectedFeatureId = useAppState((state) => state.selectedFeatureId);
  const drawFeatures = useAppState((state) => state.drawFeatures);
  const editDrawFeature = useAppState((state) => state.editDrawFeature);
  const setLocation = useAppState((state) => state.setLocation);

  const feature = drawFeatures.find(({ id }) => id === selectedFeatureId)!;
  const pointGeometry = feature.geometry as Point;
  const [coords, setCoords] = useState<[string, string]>([
    String(pointGeometry.coordinates[0]),
    String(pointGeometry.coordinates[1]),
  ]);

  useEffect(() => {
    const updatedX = round(pointGeometry.coordinates[0]);
    const updatedY = round(pointGeometry.coordinates[1]);
    Tracking.Draw.changeDotCoordinates(`${updatedX} ${updatedY}`);
    setCoords([updatedX.toString(), updatedY.toString()]);
  }, [pointGeometry.coordinates]);

  const testValid = (value: string, axis: 'x' | 'y') => {
    const pattern = axis === 'x' ? /^2[0-9]{6}(\.[0-9]*)?$/ : /^1[0-9]{6}(\.[0-9]*)?$/;
    return pattern.test(value);
  };

  const updateLocation = (x: number, y: number) => {
    editDrawFeature(feature.id as string, {
      geometry: {
        type: 'Point',
        coordinates: [x, y],
      },
    });
    setLocation({
      center: { x, y },
    });
  };

  const onChange =
    (axis: 'x' | 'y') => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const cleanValue = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
      const isValid = testValid(cleanValue, axis);

      if (axis === 'x') {
        setCoords([cleanValue, coords[1]]);
        if (isValid) {
          updateLocation(parseFloat(cleanValue) || 0, parseFloat(coords[1]));
        }
      } else {
        setCoords([coords[0], cleanValue]);
        if (isValid) {
          updateLocation(parseFloat(coords[0]), parseFloat(cleanValue) || 0);
        }
      }
    };

  return (
    <div>
      <DrawControlsLabel>{t('draw_control_coordinates_title')}</DrawControlsLabel>
      <Input
        label={t('draw_control_coordinates_label_x')}
        name="xCoord"
        value={coords[0]}
        onChange={onChange('x')}
        valid={testValid(coords[0], 'x')}
      />
      {!testValid(coords[0], 'x') && (
        <FieldError text={t('draw_control_coordinates_error_x')} />
      )}
      <Input
        label={t('draw_control_coordinates_label_y')}
        name="yCoord"
        value={coords[1]}
        onChange={onChange('y')}
        valid={testValid(coords[1], 'y')}
      />
      {!testValid(coords[1], 'y') && (
        <FieldError text={t('draw_control_coordinates_error_y')} />
      )}
    </div>
  );
};

export default CoordinatesSetting;
